import React, { CSSProperties, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ERoute, Route, routes } from "../../routes";
import { useAuthContext } from "../../context/auth-context";
import { useQuery } from "@tanstack/react-query";
import { QueryKey } from "../../services/query-keys";
import { useHeaderLinksService } from "../../services/header-links-service";
import { IHeaderLink } from "../../shared/IHeaderLink";
import { usePasswordResetModal } from "../Auth/usePasswordResetModal";

export enum EMenuItem {
  RESERVE,
  BOOKINGS,
  DYNAMIC,
  USER_MENU,
  CHANGE_PASSWORD,
  LOGOUT,
  SETTINGS
}

export interface IMenuItem {
  id?: EMenuItem;
  text: React.ReactNode;
  link?: string;
  to?: string;
  styles?: CSSProperties;
  menuItems?: IMenuItem[];
  isVisible: boolean;
  onClick?: () => void;
}

export const useMenuItems = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthContext();
  const { fetchHeaderLinks } = useHeaderLinksService();
  const openPasswordResetModal = usePasswordResetModal();

  const { data: dynamicLinks } = useQuery<IHeaderLink[]>({
    queryKey: [QueryKey.headerLinks],
    queryFn: fetchHeaderLinks,
    staleTime: 60000, // 1 minute
    enabled: !!user,
  });

  const menuItems = useMemo(() => {
    const isVisible = (routesArr: ERoute[]) => {
      return routesArr.some(route => {
        const roles = routes[route].roles;
        return roles.length === 0 || !!(user && roles.includes(user.role));
      });
    };

    let items: IMenuItem[] = [];

    if (user != null && (user.role === 'ADMIN' || user.role === 'SUPERADMIN')) {
      items.push({
        id: EMenuItem.SETTINGS,
        text: "Settings",
        to: Route.settings,
        isVisible: true,
      });
    }

    items.push(
      {
        id: EMenuItem.RESERVE,
        text: "New reservation",
        to: Route.reserve,
        isVisible: isVisible([ERoute.reserve]),
      },
      {
        id: EMenuItem.BOOKINGS,
        text: "Bookings",
        to: ERoute.reservations,
        isVisible: isVisible([ERoute.reservations]),
      }
    );

    const getLinkProps = (url?: string) => {
      if (!url) return { isVisible: true };
      if (url.startsWith('/')) return { to: url, isVisible: true };
      const hasProtocol = url.includes('://') || url.startsWith('//');
      return { link: hasProtocol ? url : `https://${url}`, isVisible: true };
    };

    // Add dynamic links from API
    if (Array.isArray(dynamicLinks) && user != null) {
      const mappedLinks: IMenuItem[] = dynamicLinks
        .filter(link => link.isVisible)
        .sort((a, b) => a.ordering - b.ordering)
        .map(link => ({
          text: link.text,
          ...getLinkProps(link.link),
          menuItems: link.sublinks
            ?.filter(sub => sub.isVisible)
            .sort((a, b) => a.ordering - b.ordering)
            .map(sub => ({
              text: sub.text,
              ...getLinkProps(sub.link),
            }))
        }));
      items = [...items, ...mappedLinks];
    }

    if (user != null) {
      const userSubItems: IMenuItem[] = [];

      userSubItems.push({
        id: EMenuItem.CHANGE_PASSWORD,
        text: "Change password",
        isVisible: true,
        onClick: () => {
          openPasswordResetModal(user.email);
        }
      });

      userSubItems.push({
        id: EMenuItem.LOGOUT,
        text: "Log out",
        isVisible: true,
        onClick: () => {
          logout();
          navigate('/auth');
        }
      });

      items.push({
        id: EMenuItem.USER_MENU,
        text: React.createElement('div', { style: { display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: "1.2" } },
          React.createElement('div', { style: { fontWeight: "bold" } }, `${user.organizationName ? user.organizationName + ' ' : ''}${user.name || user.username || ''}`),
          React.createElement('div', { style: { fontSize: "0.8rem", opacity: 0.8 } }, user.email)
        ),
        styles: { marginLeft: "auto" },
        isVisible: true,
        menuItems: userSubItems
      });
    }

    return filterMenuItems(items) as IMenuItem[];
  }, [user, dynamicLinks, navigate, logout, openPasswordResetModal]);

  return menuItems;
};

const filterMenuItems = (menuItems?: IMenuItem[]) => {
  if (!menuItems) return undefined;
  const newMenuItems = [];
  for (let menuItem of menuItems) {
    if (menuItem.isVisible) {
      menuItem.menuItems = filterMenuItems(menuItem.menuItems);
      newMenuItems.push(menuItem);
    }
  }
  return newMenuItems;
};
